import { motion } from 'framer-motion';
import { ArrowRight, Sparkles, Heart, Target, Brain } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useTheme } from '../contexts/ThemeContext';
import { Sun, Moon } from 'lucide-react';
import MimoCharacter from '../components/MimoCharacter';

export const Landing = () => {
  const { theme, toggleTheme } = useTheme();

  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const features = [
    {
      icon: Sparkles,
      title: 'Compañero Personal',
      description: 'MIMO se adapta a tu edad y estilo de vida'
    },
    {
      icon: Target,
      title: 'Hábitos Saludables',
      description: 'Crea y mantén rutinas que transforman tu vida'
    },
    {
      icon: Heart,
      title: 'Bienestar Emocional',
      description: 'Diario privado y apoyo emocional constante'
    },
    {
      icon: Brain,
      title: 'IA Empática',
      description: 'Conversaciones personalizadas que te entienden'
    }
  ];

  return (
    <div className="min-h-screen bg-background overflow-hidden">
      {/* Theme toggle */}
      <button
        onClick={toggleTheme}
        className="fixed top-6 right-6 p-3 rounded-full glass z-50"
        data-testid="theme-toggle-landing"
        aria-label="Toggle theme"
      >
        {theme === 'dark' ? (
          <Sun className="w-5 h-5 text-foreground" />
        ) : (
          <Moon className="w-5 h-5 text-foreground" />
        )}
      </button>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center px-6 py-20">
        {/* Background decoration */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-10 w-72 h-72 bg-[#FF7A59]/20 rounded-full blur-[100px]" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#4F8CFF]/20 rounded-full blur-[100px]" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-[#B794F4]/10 rounded-full blur-[120px]" />
        </div>

        <div className="relative z-10 max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left content */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="text-left"
            >
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#FF7A59]/10 text-[#FF7A59] text-sm font-medium mb-6"
              >
                <Sparkles className="w-4 h-4" />
                Tu Compañero Diario
              </motion.div>
              
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="font-adults text-5xl sm:text-6xl lg:text-7xl font-bold text-foreground mb-6 leading-tight"
              >
                Conoce a{' '}
                <span className="text-[#FF7A59]">MIMO</span>
              </motion.h1>
              
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="text-lg text-muted-foreground mb-8 max-w-lg"
              >
                Un núcleo de energía empática que te acompaña, 
                te ayuda a crear hábitos saludables y crece contigo cada día.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="flex flex-col sm:flex-row gap-4"
              >
                <Button
                  onClick={handleLogin}
                  data-testid="login-btn"
                  className="bg-[#FF7A59] hover:bg-[#E66A4B] text-white rounded-full px-8 py-6 text-lg font-semibold shadow-lg shadow-[#FF7A59]/30 transition-transform hover:scale-105"
                >
                  Comenzar Gratis
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </motion.div>

              {/* Stats */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="flex gap-8 mt-12"
              >
                <div>
                  <div className="text-3xl font-bold text-foreground">5-99</div>
                  <div className="text-sm text-muted-foreground">años</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-foreground">3</div>
                  <div className="text-sm text-muted-foreground">modos de edad</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-foreground">24/7</div>
                  <div className="text-sm text-muted-foreground">disponible</div>
                </div>
              </motion.div>
            </motion.div>

            {/* Right content - MIMO */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.4, duration: 0.8 }}
              className="flex justify-center"
            >
              <div className="relative">
                {/* Glow effect */}
                <div className="absolute inset-0 bg-[#FF7A59]/20 rounded-full blur-[60px] scale-110" />
                <MimoCharacter mood="happy" size="xl" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="relative py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="font-adults text-3xl sm:text-4xl font-bold text-foreground mb-4">
              Todo lo que necesitas
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              MIMO se adapta a tu vida, no al revés. Desde niños hasta adultos, 
              cada modo está diseñado para ti.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1, duration: 0.6 }}
                  className="group p-6 rounded-2xl glass card-hover"
                  data-testid={`feature-${index}`}
                >
                  <div className="w-12 h-12 rounded-xl bg-[#FF7A59]/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <Icon className="w-6 h-6 text-[#FF7A59]" />
                  </div>
                  <h3 className="font-semibold text-foreground mb-2">{feature.title}</h3>
                  <p className="text-sm text-muted-foreground">{feature.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Age Modes Section */}
      <section className="relative py-24 px-6 bg-muted/30">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-adults text-3xl sm:text-4xl font-bold text-foreground mb-4">
              Para todas las edades
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { 
                mode: 'Explorador', 
                age: '5-10 años', 
                desc: 'Cuentos, juegos y recompensas que hacen divertido crecer', 
                color: '#FFD166',
                energy: 'Chispas de Amabilidad'
              },
              { 
                mode: 'Compañero', 
                age: '11-17 años', 
                desc: 'Apoyo para estudios, deportes y manejo de emociones', 
                color: '#4F8CFF',
                energy: 'Impulsos de Logro'
              },
              { 
                mode: 'Aliado', 
                age: '18+ años', 
                desc: 'Productividad, hábitos y reflexión para tu día a día', 
                color: '#06D6A0',
                energy: 'Energía de Progreso'
              }
            ].map((item, index) => (
              <motion.div
                key={item.mode}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
                className="p-8 rounded-2xl glass text-center card-hover"
                data-testid={`age-mode-${item.mode.toLowerCase()}`}
              >
                <div 
                  className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center"
                  style={{ backgroundColor: `${item.color}20` }}
                >
                  <Sparkles className="w-8 h-8" style={{ color: item.color }} />
                </div>
                <h3 className="text-xl font-bold text-foreground mb-1">{item.mode}</h3>
                <p className="text-sm text-muted-foreground mb-3">{item.age}</p>
                <p className="text-muted-foreground mb-4">{item.desc}</p>
                <div 
                  className="inline-block px-3 py-1 rounded-full text-xs font-medium"
                  style={{ backgroundColor: `${item.color}20`, color: item.color }}
                >
                  {item.energy}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-24 px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-2xl mx-auto text-center"
        >
          <h2 className="font-adults text-3xl sm:text-4xl font-bold text-foreground mb-6">
            Empieza tu viaje con MIMO
          </h2>
          <p className="text-muted-foreground mb-8">
            Tu compañero te está esperando. Únete gratis y descubre 
            una nueva forma de cuidar tu bienestar.
          </p>
          <Button
            onClick={handleLogin}
            data-testid="cta-login-btn"
            className="bg-[#FF7A59] hover:bg-[#E66A4B] text-white rounded-full px-10 py-6 text-lg font-semibold shadow-lg shadow-[#FF7A59]/30 transition-transform hover:scale-105"
          >
            Crear mi MIMO
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </motion.div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-border">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-[#FF7A59] flex items-center justify-center">
              <span className="text-white font-bold">M</span>
            </div>
            <span className="font-semibold text-foreground">MIMO</span>
          </div>
          <p className="text-sm text-muted-foreground">
            © 2026 MIMO. Tu Compañero Diario.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
